import React, { useState, useEffect, useRef } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'

interface LayoutProps {
  children: React.ReactNode
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const location = useLocation()
  const navigate = useNavigate()
  const [isDirectoriesOpen, setIsDirectoriesOpen] = useState(false)
  const [isTournamentsOpen, setIsTournamentsOpen] = useState(false)
  const [isDocumentsOpen, setIsDocumentsOpen] = useState(false)
  const [isTreasuryOpen, setIsTreasuryOpen] = useState(false)
  const [isInventoryOpen, setIsInventoryOpen] = useState(false)
  const [isReportsOpen, setIsReportsOpen] = useState(false)
  const [isTelegramOpen, setIsTelegramOpen] = useState(false)
  const [isSettingsOpen, setIsSettingsOpen] = useState(false)
  const directoriesRef = useRef<HTMLDivElement>(null)
  const tournamentsRef = useRef<HTMLDivElement>(null)
  const documentsRef = useRef<HTMLDivElement>(null)
  const treasuryRef = useRef<HTMLDivElement>(null)
  const inventoryRef = useRef<HTMLDivElement>(null)
  const reportsRef = useRef<HTMLDivElement>(null)
  const telegramRef = useRef<HTMLDivElement>(null)
  const settingsRef = useRef<HTMLDivElement>(null)

  const handleLogout = () => {
    localStorage.removeItem('token')
    localStorage.removeItem('user')
    navigate('/login')
  }

  const user = JSON.parse(localStorage.getItem('user') || '{}')

  // Проверяем, находится ли текущий путь в справочниках
  const isInDirectories =
    ['/players', '/levels', '/sports', '/clubs', '/suppliers', '/inventory-items', '/warehouses', '/expense-articles', '/identities'].includes(location.pathname) ||
    location.pathname.startsWith('/identities/')
  
  // Проверяем, находится ли текущий путь в турнирах
  const isInTournaments = ['/tournaments', '/bracket-templates', '/draws', '/tournament-standings', '/match-results', '/match-formats', '/points-system', '/points-accrual-documents', '/rating-documents', '/court-reservations'].includes(location.pathname)
  
  // Проверяем, находится ли текущий путь в документах
  const isInDocuments = ['/purchases', '/settlements', '/documents/player-cancellations', '/documents/player-discounts', '/documents/player-level-changes', '/service-purchases'].includes(location.pathname)
  
  // Проверяем, находится ли текущий путь в казначействе
  const isInTreasury = ['/documents/player-payments', '/cash-expenses', '/supplier-payments', '/customer-payments', '/cashflow'].includes(location.pathname)
  
  // Проверяем, находится ли текущий путь в ТМЦ
  const isInInventory = ['/inventory-consumptions', '/inventory-returns', '/inventory-sales', '/inventory-transfers', '/purchases'].includes(location.pathname)
  
  // Проверяем, находится ли текущий путь в отчетах
  const isInReports = ['/dashboard', '/', '/reports/inventory-movements', '/reports/cash-movements', '/reports/tournaments-control', '/reports/court-rentals'].includes(location.pathname)
  
  // Проверяем, находится ли текущий путь в Telegram
  const isInTelegram = ['/telegram/logs', '/telegram/chat', '/telegram/settings', '/telegram/groups', '/telegram/notifications', '/telegram/polls', '/telegram/contests', '/telegram/registration-requests', '/telegram/broadcasts'].includes(location.pathname)
  
  // Проверяем, находится ли текущий путь в Файлах
  const isInFiles = ['/files'].includes(location.pathname)

  // Проверяем, находится ли текущий путь в Настройках
  const isInSettings = ['/settings/users', '/settings/server', '/settings/play', '/settings/match-score-entry', '/play/admin'].includes(location.pathname)

  // Закрываем выпадающие меню при клике вне их
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (directoriesRef.current && !directoriesRef.current.contains(event.target as Node)) {
        setIsDirectoriesOpen(false)
      }
      if (tournamentsRef.current && !tournamentsRef.current.contains(event.target as Node)) {
        setIsTournamentsOpen(false)
      }
      if (documentsRef.current && !documentsRef.current.contains(event.target as Node)) {
        setIsDocumentsOpen(false)
      }
      if (treasuryRef.current && !treasuryRef.current.contains(event.target as Node)) {
        setIsTreasuryOpen(false)
      }
      if (inventoryRef.current && !inventoryRef.current.contains(event.target as Node)) {
        setIsInventoryOpen(false)
      }
      if (reportsRef.current && !reportsRef.current.contains(event.target as Node)) {
        setIsReportsOpen(false)
      }
      if (telegramRef.current && !telegramRef.current.contains(event.target as Node)) {
        setIsTelegramOpen(false)
      }
      if (settingsRef.current && !settingsRef.current.contains(event.target as Node)) {
        setIsSettingsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const isChatPage = isInTelegram && location.pathname === '/telegram/chat';
  const isDrawsPage = location.pathname === '/draws';
  const isTournamentStandingsPage = location.pathname === '/tournament-standings';
  const isFixedViewportPage = isChatPage || isDrawsPage;
  
  return (
    <div className={isFixedViewportPage ? 'h-screen bg-gray-50 overflow-hidden' : 'min-h-screen bg-gray-50'}>
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <h1 className="text-xl font-bold text-gray-900">
                🎾 ERP Tennis
              </h1>
              {/* Навигация сдвинута влево рядом с логотипом */}
              <nav className="flex items-center gap-2 ml-6">
                {/* Выпадающее меню "Справочники" */}
                <div className="relative" ref={directoriesRef}>
                  <button
                    onClick={() => {
                      setIsDirectoriesOpen(!isDirectoriesOpen)
                      setIsTournamentsOpen(false)
                      setIsDocumentsOpen(false)
                      setIsTreasuryOpen(false)
                      setIsInventoryOpen(false)
                      setIsReportsOpen(false)
                      setIsTelegramOpen(false)
                      setIsSettingsOpen(false)
                    }}
                    className={`px-2.5 py-2 rounded-md text-sm font-medium flex items-center ${
                      isInDirectories
                        ? 'bg-blue-100 text-blue-700'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    📋 Справочники
                    <svg
                      className={`ml-1 h-4 w-4 transition-transform ${
                        isDirectoriesOpen ? 'rotate-180' : ''
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </button>
                  {isDirectoriesOpen && (
                    <div className="absolute left-0 mt-2 w-48 bg-white rounded-md shadow-lg z-50 border">
                      <div className="py-1">
                        <Link
                          to="/players"
                          className={`block px-4 py-2 text-sm ${
                            location.pathname === '/players'
                              ? 'bg-blue-100 text-blue-700'
                              : 'text-gray-700 hover:bg-gray-100'
                          }`}
                          onClick={() => setIsDirectoriesOpen(false)}
                        >
                          🎾 Игроки
                        </Link>
                        <Link
                          to="/identities"
                          className={`block px-4 py-2 text-sm ${
                            location.pathname === '/identities' || location.pathname.startsWith('/identities/')
                              ? 'bg-blue-100 text-blue-700'
                              : 'text-gray-700 hover:bg-gray-100'
                          }`}
                          onClick={() => setIsDirectoriesOpen(false)}
                        >
                          🧩 Идентичности
                        </Link>
                        <Link
                          to="/levels"
                          className={`block px-4 py-2 text-sm ${
                            location.pathname === '/levels'
                              ? 'bg-blue-100 text-blue-700'
                              : 'text-gray-700 hover:bg-gray-100'
                          }`}
                          onClick={() => setIsDirectoriesOpen(false)}
                        >
                          📏 Уровни
                        </Link>
                        <Link
                          to="/sports"
                          className={`block px-4 py-2 text-sm ${
                            location.pathname === '/sports'
                              ? 'bg-blue-100 text-blue-700'
                              : 'text-gray-700 hover:bg-gray-100'
                          }`}
                          onClick={() => setIsDirectoriesOpen(false)}
                        >
                          🏓 Sport
                        </Link>
                        <Link
                          to="/clubs"
                          className={`block px-4 py-2 text-sm ${
                            location.pathname === '/clubs'
                              ? 'bg-blue-100 text-blue-700'
                              : 'text-gray-700 hover:bg-gray-100'
                          }`}
                          onClick={() => setIsDirectoriesOpen(false)}
                        >
                          🏟️ Клубы
                        </Link>
                        <Link
                          to="/suppliers"
                          className={`block px-4 py-2 text-sm ${
                            location.pathname === '/suppliers'
                              ? 'bg-blue-100 text-blue-700'
                              : 'text-gray-700 hover:bg-gray-100'
                          }`}
                          onClick={() => setIsDirectoriesOpen(false)}
                        >
                          🚚 Контрагенты
                        </Link>
                        <Link
                          to="/inventory-items"
                          className={`block px-4 py-2 text-sm ${
                            location.pathname === '/inventory-items'
                              ? 'bg-blue-100 text-blue-700'
                              : 'text-gray-700 hover:bg-gray-100'
                          }`}
                          onClick={() => setIsDirectoriesOpen(false)}
                        >
                          📦 ТМЦ
                        </Link>
                        <Link
                          to="/warehouses"
                          className={`block px-4 py-2 text-sm ${
                            location.pathname === '/warehouses'
                              ? 'bg-blue-100 text-blue-700'
                              : 'text-gray-700 hover:bg-gray-100'
                          }`}
                          onClick={() => setIsDirectoriesOpen(false)}
                        >
                          🏪 Склады
                        </Link>
                        <Link
                          to="/expense-articles"
                          className={`block px-4 py-2 text-sm ${
                            location.pathname === '/expense-articles'
                              ? 'bg-blue-100 text-blue-700'
                              : 'text-gray-700 hover:bg-gray-100'
                          }`}
                          onClick={() => setIsDirectoriesOpen(false)}
                        >
                          📊 Статьи затрат
                        </Link>
                        <Link
                          to="/cash-accounts"
                          className={`block px-4 py-2 text-sm ${
                            location.pathname === '/cash-accounts'
                              ? 'bg-blue-100 text-blue-700'
                              : 'text-gray-700 hover:bg-gray-100'
                          }`}
                          onClick={() => setIsDirectoriesOpen(false)}
                        >
                          💳 Счета
                        </Link>
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Выпадающее меню "Турниры" */}
                <div className="relative" ref={tournamentsRef}>
                  <button
                    onClick={() => {
                      setIsTournamentsOpen(!isTournamentsOpen)
                      setIsDirectoriesOpen(false)
                      setIsDocumentsOpen(false)
                      setIsTreasuryOpen(false)
                      setIsInventoryOpen(false)
                      setIsReportsOpen(false)
                      setIsTelegramOpen(false)
                      setIsSettingsOpen(false)
                    }}
                    className={`px-2.5 py-2 rounded-md text-sm font-medium flex items-center ${
                      isInTournaments
                        ? 'bg-blue-100 text-blue-700'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    🏆 Турниры
                    <svg
                      className={`ml-1 h-4 w-4 transition-transform ${
                        isTournamentsOpen ? 'rotate-180' : ''
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </button>
                  {isTournamentsOpen && (
                    <div className="absolute left-0 mt-2 w-48 bg-white rounded-md shadow-lg z-50 border">
                      <div className="py-1">
                        <Link
                          to="/tournaments"
                          className={`block px-4 py-2 text-sm ${
                            location.pathname === '/tournaments'
                              ? 'bg-blue-100 text-blue-700'
                              : 'text-gray-700 hover:bg-gray-100'
                          }`}
                          onClick={() => setIsTournamentsOpen(false)}
                        >
                          🏆 Список турниров
                        </Link>
                        <Link
                          to="/bracket-templates"
                          className={`block px-4 py-2 text-sm ${
                            location.pathname === '/bracket-templates'
                              ? 'bg-blue-100 text-blue-700'
                              : 'text-gray-700 hover:bg-gray-100'
                          }`}
                          onClick={() => setIsTournamentsOpen(false)}
                        >
                          📋 Справочник сеток
                        </Link>
                        <Link
                          to="/match-formats"
                          className={`block px-4 py-2 text-sm ${
                            location.pathname === '/match-formats' ? 'bg-blue-100 text-blue-700' : 'text-gray-700 hover:bg-gray-100'
                          }`}
                          onClick={() => setIsTournamentsOpen(false)}
                        >
                          📋 Форматы счета
                        </Link>
                        <Link
                          to="/points-system"
                          className={`block px-4 py-2 text-sm ${
                            location.pathname === '/points-system' ? 'bg-blue-100 text-blue-700' : 'text-gray-700 hover:bg-gray-100'
                          }`}
                          onClick={() => setIsTournamentsOpen(false)}
                        >
                          🧾 Система начисления очков
                        </Link>
                        <Link
                          to="/points-accrual-documents"
                          className={`block px-4 py-2 text-sm ${
                            location.pathname === '/points-accrual-documents' ? 'bg-blue-100 text-blue-700' : 'text-gray-700 hover:bg-gray-100'
                          }`}
                          onClick={() => setIsTournamentsOpen(false)}
                        >
                          📒 Журнал начисления очков
                        </Link>
                        <Link
                          to="/rating-documents"
                          className={`block px-4 py-2 text-sm ${
                            location.pathname === '/rating-documents' ? 'bg-blue-100 text-blue-700' : 'text-gray-700 hover:bg-gray-100'
                          }`}
                          onClick={() => setIsTournamentsOpen(false)}
                        >
                          📗 Журнал рейтинга
                        </Link>
                        <Link
                          to="/draws"
                          className={`block px-4 py-2 text-sm ${
                            location.pathname === '/draws'
                              ? 'bg-blue-100 text-blue-700'
                              : 'text-gray-700 hover:bg-gray-100'
                          }`}
                          onClick={() => setIsTournamentsOpen(false)}
                        >
                          🎲 Жеребьёвки
                        </Link>
                        <Link
                          to="/tournament-standings"
                          className={`block px-4 py-2 text-sm ${
                            location.pathname === '/tournament-standings'
                              ? 'bg-blue-100 text-blue-700'
                              : 'text-gray-700 hover:bg-gray-100'
                          }`}
                          onClick={() => setIsTournamentsOpen(false)}
                        >
                          📊 Турнирные таблицы
                        </Link>
                        <Link
                          to="/match-results"
                          className={`block px-4 py-2 text-sm ${
                            location.pathname === '/match-results' ? 'bg-blue-100 text-blue-700' : 'text-gray-700 hover:bg-gray-100'
                          }`}
                          onClick={() => setIsTournamentsOpen(false)}
                        >
                          📝 Ввод результатов
                        </Link>
                        <Link
                          to="/court-reservations"
                          className={`block px-4 py-2 text-sm ${
                            location.pathname === '/court-reservations'
                              ? 'bg-blue-100 text-blue-700'
                              : 'text-gray-700 hover:bg-gray-100'
                          }`}
                          onClick={() => setIsTournamentsOpen(false)}
                        >
                          🎾 Резервирование кортов
                        </Link>
                      </div>
                    </div>
                  )}
                </div>

                {/* Выпадающее меню "Документы" */}
                <div className="relative" ref={documentsRef}>
                  <button
                    onClick={() => {
                      setIsDocumentsOpen(!isDocumentsOpen)
                      setIsDirectoriesOpen(false)
                      setIsTournamentsOpen(false)
                      setIsTreasuryOpen(false)
                      setIsInventoryOpen(false)
                      setIsReportsOpen(false)
                      setIsTelegramOpen(false)
                      setIsSettingsOpen(false)
                    }}
                    className={`px-2.5 py-2 rounded-md text-sm font-medium flex items-center ${
                      isInDocuments
                        ? 'bg-blue-100 text-blue-700'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    📄 Документы
                    <svg
                      className={`ml-1 h-4 w-4 transition-transform ${
                        isDocumentsOpen ? 'rotate-180' : ''
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </button>
                  {isDocumentsOpen && (
                    <div className="absolute left-0 mt-2 w-48 bg-white rounded-md shadow-lg z-50 border">
                      <div className="py-1">
                        <Link
                          to="/settlements"
                          className={`block px-4 py-2 text-sm ${
                            location.pathname === '/settlements'
                              ? 'bg-blue-100 text-blue-700'
                              : 'text-gray-700 hover:bg-gray-100'
                          }`}
                          onClick={() => setIsDocumentsOpen(false)}
                        >
                          📑 Задолженность
                        </Link>
                        <Link
                          to="/documents/player-cancellations"
                          className={`block px-4 py-2 text-sm ${
                            location.pathname === '/documents/player-cancellations'
                              ? 'bg-blue-100 text-blue-700'
                              : 'text-gray-700 hover:bg-gray-100'
                          }`}
                          onClick={() => setIsDocumentsOpen(false)}
                        >
                          ❌ Отмены участий
                        </Link>
                        <Link
                          to="/documents/player-discounts"
                          className={`block px-4 py-2 text-sm ${
                            location.pathname === '/documents/player-discounts'
                              ? 'bg-blue-100 text-blue-700'
                              : 'text-gray-700 hover:bg-gray-100'
                          }`}
                          onClick={() => setIsDocumentsOpen(false)}
                        >
                          🏷️ Коррекции взносов
                        </Link>
                        <Link
                          to="/documents/player-level-changes"
                          className={`block px-4 py-2 text-sm ${
                            location.pathname === '/documents/player-level-changes'
                              ? 'bg-blue-100 text-blue-700'
                              : 'text-gray-700 hover:bg-gray-100'
                          }`}
                          onClick={() => setIsDocumentsOpen(false)}
                        >
                          📘 Изменение уровня игрока
                        </Link>
                        <Link
                          to="/service-purchases"
                          className={`block px-4 py-2 text-sm ${
                            location.pathname === '/service-purchases'
                              ? 'bg-blue-100 text-blue-700'
                              : 'text-gray-700 hover:bg-gray-100'
                          }`}
                          onClick={() => setIsDocumentsOpen(false)}
                        >
                          🏟️ Закупка услуг
                        </Link>
                      </div>
                    </div>
                  )}
                </div>

                {/* Выпадающее меню "Казначейство" */}
                <div className="relative" ref={treasuryRef}>
                  <button
                    onClick={() => {
                      setIsTreasuryOpen(!isTreasuryOpen)
                      setIsDirectoriesOpen(false)
                      setIsTournamentsOpen(false)
                      setIsDocumentsOpen(false)
                      setIsInventoryOpen(false)
                      setIsReportsOpen(false)
                      setIsTelegramOpen(false)
                      setIsSettingsOpen(false)
                    }}
                    className={`px-2.5 py-2 rounded-md text-sm font-medium flex items-center ${
                      isInTreasury
                        ? 'bg-blue-100 text-blue-700'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    💰 Казначейство
                    <svg
                      className={`ml-1 h-4 w-4 transition-transform ${
                        isTreasuryOpen ? 'rotate-180' : ''
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </button>
                  {isTreasuryOpen && (
                    <div className="absolute left-0 mt-2 w-48 bg-white rounded-md shadow-lg z-50 border">
                      <div className="py-1">
                        <Link
                          to="/documents/player-payments"
                          className={`block px-4 py-2 text-sm ${
                            location.pathname === '/documents/player-payments'
                              ? 'bg-blue-100 text-blue-700'
                              : 'text-gray-700 hover:bg-gray-100'
                          }`}
                          onClick={() => setIsTreasuryOpen(false)}
                        >
                          💵 Оплаты игроков
                        </Link>
                        <Link
                          to="/cash-expenses"
                          className={`block px-4 py-2 text-sm ${
                            location.pathname === '/cash-expenses'
                              ? 'bg-blue-100 text-blue-700'
                              : 'text-gray-700 hover:bg-gray-100'
                          }`}
                          onClick={() => setIsTreasuryOpen(false)}
                        >
                          💸 Расход денег
                        </Link>
                        <Link
                          to="/supplier-payments"
                          className={`block px-4 py-2 text-sm ${
                            location.pathname === '/supplier-payments'
                              ? 'bg-blue-100 text-blue-700'
                              : 'text-gray-700 hover:bg-gray-100'
                          }`}
                          onClick={() => setIsTreasuryOpen(false)}
                        >
                          💳 Оплата поставщику
                        </Link>
                        <Link
                          to="/customer-payments"
                          className={`block px-4 py-2 text-sm ${
                            location.pathname === '/customer-payments'
                              ? 'bg-blue-100 text-blue-700'
                              : 'text-gray-700 hover:bg-gray-100'
                          }`}
                          onClick={() => setIsTreasuryOpen(false)}
                        >
                          💰 Оплата покупателя
                        </Link>
                        <Link
                          to="/cashflow"
                          className={`block px-4 py-2 text-sm ${
                            location.pathname === '/cashflow'
                              ? 'bg-blue-100 text-blue-700'
                              : 'text-gray-700 hover:bg-gray-100'
                          }`}
                          onClick={() => setIsTreasuryOpen(false)}
                        >
                          💵 Перемещение денег
                        </Link>
                      </div>
                    </div>
                  )}
                </div>

                {/* Выпадающее меню "ТМЦ" */}
                <div className="relative" ref={inventoryRef}>
                  <button
                    onClick={() => {
                      setIsInventoryOpen(!isInventoryOpen)
                      setIsDirectoriesOpen(false)
                      setIsTournamentsOpen(false)
                      setIsDocumentsOpen(false)
                      setIsTreasuryOpen(false)
                      setIsReportsOpen(false)
                      setIsTelegramOpen(false)
                      setIsSettingsOpen(false)
                    }}
                    className={`px-2.5 py-2 rounded-md text-sm font-medium flex items-center ${
                      isInInventory
                        ? 'bg-blue-100 text-blue-700'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    📦 ТМЦ
                    <svg
                      className={`ml-1 h-4 w-4 transition-transform ${
                        isInventoryOpen ? 'rotate-180' : ''
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </button>
                  {isInventoryOpen && (
                    <div className="absolute left-0 mt-2 w-48 bg-white rounded-md shadow-lg z-50 border">
                      <div className="py-1">
                        <Link
                          to="/purchases"
                          className={`block px-4 py-2 text-sm ${
                            location.pathname === '/purchases'
                              ? 'bg-blue-100 text-blue-700'
                              : 'text-gray-700 hover:bg-gray-100'
                          }`}
                          onClick={() => setIsInventoryOpen(false)}
                        >
                          🛍️ Закупки ТМЦ
                        </Link>
                        <Link
                          to="/inventory-consumptions"
                          className={`block px-4 py-2 text-sm ${
                            location.pathname === '/inventory-consumptions'
                              ? 'bg-blue-100 text-blue-700'
                              : 'text-gray-700 hover:bg-gray-100'
                          }`}
                          onClick={() => setIsInventoryOpen(false)}
                        >
                          📤 Расход ТМЦ
                        </Link>
                        <Link
                          to="/inventory-returns"
                          className={`block px-4 py-2 text-sm ${
                            location.pathname === '/inventory-returns'
                              ? 'bg-blue-100 text-blue-700'
                              : 'text-gray-700 hover:bg-gray-100'
                          }`}
                          onClick={() => setIsInventoryOpen(false)}
                        >
                          📥 Возврат ТМЦ
                        </Link>
                        <Link
                          to="/inventory-sales"
                          className={`block px-4 py-2 text-sm ${
                            location.pathname === '/inventory-sales'
                              ? 'bg-blue-100 text-blue-700'
                              : 'text-gray-700 hover:bg-gray-100'
                          }`}
                          onClick={() => setIsInventoryOpen(false)}
                        >
                          💰 Продажа ТМЦ
                        </Link>
                        <Link
                          to="/inventory-transfers"
                          className={`block px-4 py-2 text-sm ${
                            location.pathname === '/inventory-transfers'
                              ? 'bg-blue-100 text-blue-700'
                              : 'text-gray-700 hover:bg-gray-100'
                          }`}
                          onClick={() => setIsInventoryOpen(false)}
                        >
                          🔄 Перемещение ТМЦ
                        </Link>
                      </div>
                    </div>
                  )}
                </div>

                {/* Выпадающее меню "Отчеты" */}
                <div className="relative" ref={reportsRef}>
                  <button
                    onClick={() => {
                      setIsReportsOpen(!isReportsOpen)
                      setIsDirectoriesOpen(false)
                      setIsTournamentsOpen(false)
                      setIsDocumentsOpen(false)
                      setIsTreasuryOpen(false)
                      setIsInventoryOpen(false)
                      setIsTelegramOpen(false)
                      setIsSettingsOpen(false)
                    }}
                    className={`px-2.5 py-2 rounded-md text-sm font-medium flex items-center ${
                      isInReports
                        ? 'bg-blue-100 text-blue-700'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    📊 Отчеты
                    <svg
                      className={`ml-1 h-4 w-4 transition-transform ${
                        isReportsOpen ? 'rotate-180' : ''
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </button>
                  {isReportsOpen && (
                    <div className="absolute left-0 mt-2 w-56 bg-white rounded-md shadow-lg z-50 border">
                      <div className="py-1">
                        <Link
                          to="/dashboard"
                          className={`block px-4 py-2 text-sm ${
                            location.pathname === '/dashboard' || location.pathname === '/'
                              ? 'bg-blue-100 text-blue-700'
                              : 'text-gray-700 hover:bg-gray-100'
                          }`}
                          onClick={() => setIsReportsOpen(false)}
                        >
                          🏠 Дашборд
                        </Link>
                        <Link
                          to="/reports/inventory-movements"
                          className={`block px-4 py-2 text-sm ${
                            location.pathname === '/reports/inventory-movements'
                              ? 'bg-blue-100 text-blue-700'
                              : 'text-gray-700 hover:bg-gray-100'
                          }`}
                          onClick={() => setIsReportsOpen(false)}
                        >
                          📈 Движение ТМЦ
                        </Link>
                        <Link
                          to="/reports/settlements"
                          className={`block px-4 py-2 text-sm ${
                            location.pathname === '/reports/settlements'
                              ? 'bg-blue-100 text-blue-700'
                              : 'text-gray-700 hover:bg-gray-100'
                          }`}
                          onClick={() => setIsReportsOpen(false)}
                        >
                          💳 Взаиморасчеты
                        </Link>
                        <Link
                          to="/reports/player-settlements"
                          className={`block px-4 py-2 text-sm ${
                            location.pathname === '/reports/player-settlements'
                              ? 'bg-blue-100 text-blue-700'
                              : 'text-gray-700 hover:bg-gray-100'
                          }`}
                          onClick={() => setIsReportsOpen(false)}
                        >
                          🧑‍🎾 Взаиморасчеты с игроками
                        </Link>
                        <Link
                          to="/reports/cash-movements"
                          className={`block px-4 py-2 text-sm ${
                            location.pathname === '/reports/cash-movements'
                              ? 'bg-blue-100 text-blue-700'
                              : 'text-gray-700 hover:bg-gray-100'
                          }`}
                          onClick={() => setIsReportsOpen(false)}
                        >
                          💵 Движение денег
                        </Link>
                        <Link
                          to="/reports/profit"
                          className={`block px-4 py-2 text-sm ${
                            location.pathname === '/reports/profit'
                              ? 'bg-blue-100 text-blue-700'
                              : 'text-gray-700 hover:bg-gray-100'
                          }`}
                          onClick={() => setIsReportsOpen(false)}
                        >
                          💰 Прибыль
                        </Link>
                        <Link
                          to="/reports/tournaments-control"
                          className={`block px-4 py-2 text-sm ${
                            location.pathname === '/reports/tournaments-control'
                              ? 'bg-blue-100 text-blue-700'
                              : 'text-gray-700 hover:bg-gray-100'
                          }`}
                          onClick={() => setIsReportsOpen(false)}
                        >
                          🔍 Контроль турниров
                        </Link>
                        <Link
                          to="/reports/court-rentals"
                          className={`block px-4 py-2 text-sm ${
                            location.pathname === '/reports/court-rentals'
                              ? 'bg-blue-100 text-blue-700'
                              : 'text-gray-700 hover:bg-gray-100'
                          }`}
                          onClick={() => setIsReportsOpen(false)}
                        >
                          🎾 Аренда кортов
                        </Link>
                      </div>
                    </div>
                  )}
                </div>

                {/* Выпадающее меню "Telegram" */}
                <div className="relative" ref={telegramRef}>
                  <button
                    onClick={() => {
                      setIsTelegramOpen(!isTelegramOpen)
                      setIsDirectoriesOpen(false)
                      setIsTournamentsOpen(false)
                      setIsDocumentsOpen(false)
                      setIsTreasuryOpen(false)
                      setIsInventoryOpen(false)
                      setIsReportsOpen(false)
                      setIsSettingsOpen(false)
                    }}
                    className={`px-2.5 py-2 rounded-md text-sm font-medium flex items-center ${
                      isInTelegram
                        ? 'bg-blue-100 text-blue-700'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    📱 Telegram
                    <svg
                      className={`ml-1 h-4 w-4 transition-transform ${
                        isTelegramOpen ? 'rotate-180' : ''
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </button>
                  {isTelegramOpen && (
                    <div className="absolute left-0 mt-2 w-48 bg-white rounded-md shadow-lg z-50 border">
                      <div className="py-1">
                        <Link
                          to="/telegram/logs"
                          className={`block px-4 py-2 text-sm ${
                            location.pathname === '/telegram/logs'
                              ? 'bg-blue-100 text-blue-700'
                              : 'text-gray-700 hover:bg-gray-100'
                          }`}
                          onClick={() => setIsTelegramOpen(false)}
                        >
                          📋 Логи сообщений
                        </Link>
                        <Link
                          to="/telegram/chat"
                          className={`block px-4 py-2 text-sm ${
                            location.pathname === '/telegram/chat'
                              ? 'bg-blue-100 text-blue-700'
                              : 'text-gray-700 hover:bg-gray-100'
                          }`}
                          onClick={() => setIsTelegramOpen(false)}
                        >
                          💬 Чат
                        </Link>
                        <Link
                          to="/telegram/settings"
                          className={`block px-4 py-2 text-sm ${
                            location.pathname === '/telegram/settings'
                              ? 'bg-blue-100 text-blue-700'
                              : 'text-gray-700 hover:bg-gray-100'
                          }`}
                          onClick={() => setIsTelegramOpen(false)}
                        >
                          ⚙️ Настройки
                        </Link>
                        <Link
                          to="/telegram/groups"
                          className={`block px-4 py-2 text-sm ${
                            location.pathname === '/telegram/groups'
                              ? 'bg-blue-100 text-blue-700'
                              : 'text-gray-700 hover:bg-gray-100'
                          }`}
                          onClick={() => setIsTelegramOpen(false)}
                        >
                          👥 Настройка групп
                        </Link>
                        <Link
                          to="/telegram/notifications"
                          className={`block px-4 py-2 text-sm ${
                            location.pathname === '/telegram/notifications'
                              ? 'bg-blue-100 text-blue-700'
                              : 'text-gray-700 hover:bg-gray-100'
                          }`}
                          onClick={() => setIsTelegramOpen(false)}
                        >
                          🔔 Уведомления
                        </Link>
                        <Link
                          to="/telegram/polls"
                          className={`block px-4 py-2 text-sm ${
                            location.pathname === '/telegram/polls'
                              ? 'bg-blue-100 text-blue-700'
                              : 'text-gray-700 hover:bg-gray-100'
                          }`}
                          onClick={() => setIsTelegramOpen(false)}
                        >
                          📊 Голосования
                        </Link>
                        <Link
                          to="/telegram/contests"
                          className={`block px-4 py-2 text-sm ${
                            location.pathname === '/telegram/contests'
                              ? 'bg-blue-100 text-blue-700'
                              : 'text-gray-700 hover:bg-gray-100'
                          }`}
                          onClick={() => setIsTelegramOpen(false)}
                        >
                          🏆 Конкурсы
                        </Link>
                        <Link
                          to="/telegram/registration-requests"
                          className={`block px-4 py-2 text-sm ${
                            location.pathname === '/telegram/registration-requests'
                              ? 'bg-blue-100 text-blue-700'
                              : 'text-gray-700 hover:bg-gray-100'
                          }`}
                          onClick={() => setIsTelegramOpen(false)}
                        >
                          🧾 Заявки на регистрацию
                        </Link>
                        <Link
                          to="/telegram/broadcasts"
                          className={`block px-4 py-2 text-sm ${
                            location.pathname === '/telegram/broadcasts'
                              ? 'bg-blue-100 text-blue-700'
                              : 'text-gray-700 hover:bg-gray-100'
                          }`}
                          onClick={() => setIsTelegramOpen(false)}
                        >
                          📣 Рассылки
                        </Link>
                      </div>
                    </div>
                  )}
                </div>
                {/* Выпадающее меню "Настройки" */}
                <div className="relative" ref={settingsRef}>
                  <button
                    onClick={() => {
                      setIsSettingsOpen(!isSettingsOpen)
                      setIsDirectoriesOpen(false)
                      setIsTournamentsOpen(false)
                      setIsDocumentsOpen(false)
                      setIsTreasuryOpen(false)
                      setIsInventoryOpen(false)
                      setIsReportsOpen(false)
                      setIsTelegramOpen(false)
                    }}
                    className={`px-2.5 py-2 rounded-md text-sm font-medium flex items-center ${
                      isInSettings
                        ? 'bg-blue-100 text-blue-700'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    ⚙️ Настройки
                    <svg
                      className={`ml-1 h-4 w-4 transition-transform ${
                        isSettingsOpen ? 'rotate-180' : ''
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </button>
                  {isSettingsOpen && (
                    <div className="absolute left-0 mt-2 w-48 bg-white rounded-md shadow-lg z-50 border">
                      <div className="py-1">
                        <Link
                          to="/settings/users"
                          className={`block px-4 py-2 text-sm ${
                            location.pathname === '/settings/users'
                              ? 'bg-blue-100 text-blue-700'
                              : 'text-gray-700 hover:bg-gray-100'
                          }`}
                          onClick={() => setIsSettingsOpen(false)}
                        >
                          👤 Пользователи
                        </Link>
                        <Link
                          to="/settings/server"
                          className={`block px-4 py-2 text-sm ${
                            location.pathname === '/settings/server'
                              ? 'bg-blue-100 text-blue-700'
                              : 'text-gray-700 hover:bg-gray-100'
                          }`}
                          onClick={() => setIsSettingsOpen(false)}
                        >
                          🖥️ Сервер
                        </Link>
                        <Link
                          to="/settings/play"
                          className={`block px-4 py-2 text-sm ${
                            location.pathname === '/settings/play'
                              ? 'bg-blue-100 text-blue-700'
                              : 'text-gray-700 hover:bg-gray-100'
                          }`}
                          onClick={() => setIsSettingsOpen(false)}
                        >
                          🎾 TTM PLAY
                        </Link>
                        <Link
                          to="/settings/match-score-entry"
                          className={`block px-4 py-2 text-sm ${
                            location.pathname === '/settings/match-score-entry'
                              ? 'bg-blue-100 text-blue-700'
                              : 'text-gray-700 hover:bg-gray-100'
                          }`}
                          onClick={() => setIsSettingsOpen(false)}
                        >
                          🧪 Score Entry V2
                        </Link>
                        <Link
                          to="/play/admin"
                          className={`block px-4 py-2 text-sm ${
                            location.pathname === '/play/admin'
                              ? 'bg-blue-100 text-blue-700'
                              : 'text-gray-700 hover:bg-gray-100'
                          }`}
                          onClick={() => setIsSettingsOpen(false)}
                        >
                          🎾 TTM PLAY Admin
                        </Link>
                      </div>
                    </div>
                  )}
                </div>
                {/* Пункт меню "Файлы" */}
                <Link
                  to="/files"
                  className={`px-2.5 py-2 rounded-md text-sm font-medium ${
                    isInFiles
                      ? 'bg-blue-100 text-blue-700'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  📁 Файлы
                </Link>
              </nav>
            </div>
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <button
                  onClick={handleLogout}
                  className="px-3 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md"
                >
                  Выйти
                </button>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main
        className={`${
          isChatPage
            ? 'p-0 max-w-none mx-0'
            : isDrawsPage
              ? 'max-w-[1600px] mx-auto py-4 sm:px-6 lg:px-8'
              : isTournamentStandingsPage
                ? 'max-w-[1600px] mx-auto py-6 px-1 sm:px-2 lg:px-2'
              : 'max-w-[1600px] mx-auto py-6 sm:px-6 lg:px-8'
        }`}
        style={isFixedViewportPage ? { height: 'calc(100vh - 64px)', overflow: 'hidden' } : {}}
      >
        {children}
      </main>
    </div>
  )
}

export default Layout
